import type {ChangeEvent, ReactElement, ReactNode} from 'react';
import {useRef} from 'react';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {DragDropOverlay} from '@Components/drag-drop-overlay/drag-drop-overlay';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import type {AiContentVerifyDropzoneProps} from './ai-content-verify-dropzone.types';
import {DROPZONE_ERROR_ID} from './ai-content-verify-dropzone.types';
import styles from './ai-content-verify-dropzone.module.scss';

export function AiContentVerifyDropzone({limitsText, errorMessage, fileInputAccept, onFileChosen}: AiContentVerifyDropzoneProps): ReactElement {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onChooseFileClick = (): void => {
    fileInputRef.current?.click();
  };

  const onFileInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    onFileChosen(file);
  };

  const onFilesDropped = (files: File[]): void => {
    onFileChosen(files[0]);
  };

  const renderErrorMessage = (): ReactElement | null => {
    if (!errorMessage) {
      return null;
    }

    return (
      <div id={DROPZONE_ERROR_ID} role="alert" className={styles.dropzoneError}>
        <Icon icon="icon-info" size={IconSize.SIZE_ICON_16} ariaHidden />
        <Text val={errorMessage} size={TextSize.MD} />
      </div>
    );
  };

  return (
    <div className={styles.dropzone}>
      <DragDropOverlay onHoverContent={renderDropOverlayContent()} onFilesUpload={onFilesDropped} className={styles.dropzoneOverlay} />
      <div className={`${styles.dropzoneArea} ${errorMessage ? styles.dropzoneAreaError : ''}`}>
        <Icon icon="icon-upload" size={IconSize.SIZE_ICON_24} type={IconType.FLAT} shape={IconShape.CIRCLE} className={styles.dropzoneIcon} ariaHidden />
        <Text val={window.i18next.t('pmwjs_ai_verify_drop_title')} size={TextSize.LG} bold className={styles.dropzoneTitle} />
        <Text val={window.i18next.t('pmwjs_ai_verify_drop_or')} size={TextSize.MD} className={styles.dropzoneSecondaryText} />
        <Button
          text={window.i18next.t('pmwjs_ai_verify_choose_file')}
          icon="icon-upload"
          iconPlacement={IconPlacement.LEFT}
          type={Type.PRIMARY}
          size={Size.SMALL}
          onClick={onChooseFileClick}
          attributes={errorMessage ? {'aria-describedby': DROPZONE_ERROR_ID} : undefined}
        />
        <input ref={fileInputRef} type="file" accept={fileInputAccept} className={styles.dropzoneFileInput} tabIndex={-1} aria-hidden onChange={onFileInputChange} />
        <Text val={limitsText} size={TextSize.SM} className={styles.dropzoneSecondaryText} />
      </div>
      {renderErrorMessage()}
    </div>
  );
}

function renderDropOverlayContent(): ReactNode {
  return (
    <div className={styles.dropzoneOverlayContent}>
      <Icon icon="icon-upload" size={IconSize.SIZE_ICON_32} type={IconType.FLAT} shape={IconShape.CIRCLE} ariaHidden />
      <Text val={window.i18next.t('pmwjs_ai_verify_drop_overlay')} size={TextSize.LG} bold className={styles.dropzoneOverlayText} />
    </div>
  );
}
